/**
 * recently-viewed.ts — recently-viewed products cache (localStorage).
 *
 * Spec ref: SPEC-MARKETPLACE-QA-SMOKE-TEST-2026-10-03 item M-7
 *           (recently viewed products rail on the catalog page).
 *
 * Storage shape:
 *   - Key: `profileia:recently-viewed`
 *   - Value: JSON array of `RecentlyViewedItem`, max 12 items, most
 *     recent first, deduped by `productId`.
 *
 * The shape extends the spec's minimum payload
 * (`{productId, slug, storeSlug, title, priceCredits, images[0]}`)
 * with the few extra fields `<ProductCard />` reads from
 * `PublicProductResponse` (type, category, rating, sellerStoreName,
 * singlePurchaseRecurring). Without these extra fields we would either
 * have to (a) fetch every recently-viewed product from the API on each
 * catalog load (defeats the purpose of a lightweight client cache) or
 * (b) build a parallel "recent card" component (CR-CHECKLIST §H2 —
 * fix de raíz, no parallel mechanisms). Storing the small extra payload
 * is the correct fix: same source of truth (PublicProductResponse), no
 * new card component, no extra API call.
 *
 * §6 Anti-Invención:
 *   - localStorage key follows the existing `profileia:*` namespace
 *     (mirrors `profileia:marketplace-filter` + `profileia:buyer-location`
 *     in `market/page.tsx` — single namespace convention).
 *   - NO `as any` (P-041). NO `console.*` (P-042). The helpers are
 *     SSR-safe (no-op on the server — `typeof window === 'undefined'`).
 *   - Defensive JSON parse (catches malformed payloads from old
 *     versions / hand-edited localStorage — same pattern as the
 *     `profileia:buyer-location` reader in `market/page.tsx`).
 */

import type { ProductType } from '@profileia/shared';

/**
 * localStorage key — `profileia:` namespace (mirrors
 * `profileia:marketplace-filter` + `profileia:buyer-location`).
 */
export const RECENTLY_VIEWED_KEY = 'profileia:recently-viewed';

/**
 * Max items stored in the cache. Per the M-7 spec: "max 12 items".
 * Older items (beyond position 12) are dropped on every write.
 */
export const RECENTLY_VIEWED_MAX_ITEMS = 12;

/**
 * Max items rendered in the catalog rail. Per the M-7 spec: "displays
 * the last 8 recently viewed products". The cache stores 12 (so the
 * user can navigate to 12 different products + still see 8 in the
 * rail); the rail slices to the most recent 8.
 */
export const RECENTLY_VIEWED_RAIL_MAX_ITEMS = 8;

/**
 * A single recently-viewed product entry. The shape is a strict subset
 * of `PublicProductResponse` — enough to render a `<ProductCard />`
 * without an extra API call. See the file header for the rationale
 * behind the extra fields beyond the spec's minimum payload.
 */
export interface RecentlyViewedItem {
  productId: string;
  slug: string;
  storeSlug: string;
  title: string;
  priceCredits: string;
  /** First image only (the rail card doesn't need the full gallery). */
  images: string[];
  /** ProductType at the time of viewing — used for the type badge. */
  type: ProductType;
  /** Category slug at the time of viewing — used for the category badge. */
  category: string;
  /** Product rating at the time of viewing (0 = "Sin reseñas"). */
  rating: number;
  /** Seller storeName at the time of viewing — used for the seller line. */
  sellerStoreName: string;
  /** Optional — used for the "Recurrente" badge (mirrors PublicProductResponse). */
  singlePurchaseRecurring?: boolean;
  /** ISO timestamp of when the product was viewed — used to dedupe + order. */
  viewedAt: string;
}

/**
 * Read the recently-viewed cache from localStorage.
 *
 * SSR-safe: returns `[]` on the server (no `window`).
 * Defensive: returns `[]` on malformed JSON (mirrors the
 * `profileia:buyer-location` reader pattern in `market/page.tsx`).
 */
export function loadRecentlyViewed(): RecentlyViewedItem[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(RECENTLY_VIEWED_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    // Filter out malformed entries (missing required fields) rather
    // than throwing — same defensive pattern as `getReferralCookie`.
    return parsed.filter(
      (it): it is RecentlyViewedItem =>
        typeof it === 'object' &&
        it !== null &&
        typeof (it as RecentlyViewedItem).productId === 'string' &&
        typeof (it as RecentlyViewedItem).slug === 'string' &&
        typeof (it as RecentlyViewedItem).storeSlug === 'string',
    );
  } catch {
    return [];
  }
}

/**
 * Save (or rather, prepend) a product to the recently-viewed cache.
 *
 * Behavior:
 *   - Dedupes by `productId` (if the product is already in the cache,
 *     it's moved to position 0 with the new `viewedAt`).
 *   - Prepends the new item (most-recent-first ordering).
 *   - Trims to `RECENTLY_VIEWED_MAX_ITEMS` (12) entries.
 *   - SSR-safe + try/catch on `localStorage.setItem` (private mode /
 *     quota exceeded — silently ignored, same pattern as
 *     `market/page.tsx` tab persistence).
 */
export function saveRecentlyViewed(item: Omit<RecentlyViewedItem, 'viewedAt'>): void {
  if (typeof window === 'undefined') return;
  try {
    const current = loadRecentlyViewed();
    const deduped = current.filter((it) => it.productId !== item.productId);
    const next: RecentlyViewedItem[] = [
      { ...item, viewedAt: new Date().toISOString() },
      ...deduped,
    ].slice(0, RECENTLY_VIEWED_MAX_ITEMS);
    window.localStorage.setItem(RECENTLY_VIEWED_KEY, JSON.stringify(next));
  } catch {
    // localStorage unavailable (private mode) or quota exceeded —
    // silently ignore. The rail just won't show this product; the
    // rest of the page works normally.
  }
}
